import { notifyError, notifySuccess, notifyWarning } from '../Notify';
import { isPhoneValid } from './validation';
import { validatePasswordComplete } from './validation';
import { checkIfUserPhoneExists } from '../../actions/userPhoneAction';
import { phoneNumberSignUp } from '../../pages/account/phoneNumberLogin/services/phoneAuthService';
import GetUserPhoneInfo from '../../pages/account/phoneNumberLogin/GetUserPhoneInfo';
import {
  savePhoneNumberUser,
  saveUserWithFid,
} from '../../actions/userActions';
import {
  VALIDATION_CONSTANTS,
  FIREBASE_STORAGE_KEYS,
  USER_MESSAGES,
  AUTH_STEPS,
  ERROR_TYPES,
} from '../../constants/authConstants';

// ============================================
// PHONE NUMBER SUBMISSION (SMS)
// ============================================

/**
 * Checks if rate limiting should be skipped based on last attempt
 */
export const shouldSkipRateLimit = (lastAttemptDate, currentTime) => {
  if (!lastAttemptDate) return false;
  return currentTime - Number.parseInt(lastAttemptDate) < VALIDATION_CONSTANTS.RATE_LIMIT_DELAY_MS;
};

/**
 * Handles phone number submission for authentication flow
 */
export const handlePhoneNumberSubmission = async ({
  phoneNumber,
  auth,
  axiosInstance,
  onUserExists,
  onUserNew,
  onError,
  setLoading,
  onSmsSending,
  recaptchaContainerId = 'recaptcha-container',
}) => {
  const formattedNumber = phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`;

  if (formattedNumber.length < VALIDATION_CONSTANTS.MIN_PHONE_LENGTH) {
    notifyWarning(USER_MESSAGES.INVALID_NUMBER);
    return { success: false, error: ERROR_TYPES.INVALID_NUMBER };
  }

  setLoading(true);

  try {
    const userCheck = await checkIfUserPhoneExists(axiosInstance, formattedNumber);
    console.info({ userCheck });

    if (userCheck.success) {
      if (onUserExists) onUserExists(formattedNumber);
      setLoading(false);
      return { success: true, userExists: true, phoneNumber: formattedNumber };
    }

    if (userCheck.serverError) {
      notifyError(USER_MESSAGES.USER_CHECK_ERROR);
      setLoading(false);
      if (onError) onError(userCheck.serverError);
      return { success: false, error: ERROR_TYPES.USER_CHECK_ERROR };
    }

    const lastAttemptDate = localStorage.getItem(FIREBASE_STORAGE_KEYS.LAST_ATTEMPT_DATE);
    const currentTime = new Date().getTime();

    if (shouldSkipRateLimit(lastAttemptDate, currentTime)) {
      if (onUserNew) onUserNew(formattedNumber);
      setLoading(false);
      return { success: true, userExists: false, skippedRateLimit: true, phoneNumber: formattedNumber };
    }

    if (!isPhoneValid(formattedNumber)) {
      notifyWarning(USER_MESSAGES.INVALID_NUMBER);
      setLoading(false);
      return { success: false, error: ERROR_TYPES.INVALID_NUMBER };
    }

    if (onSmsSending) onSmsSending();

    const result = await phoneNumberSignUp(
      auth,
      formattedNumber,
      null,
      'signup',
      recaptchaContainerId,
      setLoading
    );

    if (result?.success) {
      localStorage.setItem(FIREBASE_STORAGE_KEYS.LAST_ATTEMPT_DATE, currentTime.toString());
      if (onUserNew) onUserNew(formattedNumber);
      return { success: true, userExists: false, smsSent: true, phoneNumber: formattedNumber };
    }

    if (onError) onError(result?.error);
    handleSignUpError(result?.error);
    setLoading(false);

    return { success: false, error: result?.error };
  } catch (error) {
    console.error('Unexpected error:', error);
    notifyError(USER_MESSAGES.UNEXPECTED_ERROR);
    setLoading(false);
    if (onError) onError(error);
    return { success: false, error: ERROR_TYPES.SIGNUP_ERROR };
  }
};

/**
 * Handles signup errors and displays appropriate messages
 */
export const handleSignUpError = (error) => {
  if (!error) return;

  if (error === ERROR_TYPES.RATE_LIMITED) {
    notifyError('Trop de tentatives. Réessaie plus tard.');
  } else if (error === ERROR_TYPES.USER_CANCELLED) {
    notifyWarning('Opération annulée');
  } else if (error.includes('too-many-requests')) {
    notifyError('Trop de requêtes. Patiente un instant avant de réessayer.');
  } else {
    notifyError('Erreur lors de l\'envoi du code SMS');
  }
};

// ============================================
// PASSWORD LOGIN
// ============================================

/**
 * Gets user phone info and handles redirection after authentication
 */
export const handlePhoneLogin = async ({
  auth,
  setLoading,
  dispatch,
  axiosInstance,
  cartItems,
  navigate,
  userResult,
  password,
  action,
  phoneNumber,
  setLockUntil,
  setError,
}) => {
  try {
    const authData = action === 'signup'
      ? auth
      : { currentUser: { phoneNumber } };

    const data = await GetUserPhoneInfo(authData, userResult, password);

    if (data.newResult.profile.fidCardNum) {
      const fidCardNum = data.newResult.profile.fidCardNum;
      await dispatch(
        saveUserWithFid(
          data.newResult.profile,
          axiosInstance,
          cartItems,
          fidCardNum,
          navigate,
        ),
      );
    } else {
      await dispatch(
        savePhoneNumberUser(
          data.newResult.profile,
          axiosInstance,
          cartItems,
          navigate,
          action,
          setLockUntil,
          setError,
          setLoading,
        ),
      );
    }
    return true;
  } catch (error) {
    console.error('Error during authentication:', error);
    notifyError('Erreur lors de l\'authentification');
    return false;
  }
};

/**
 * Validates and submits password for phone authentication
 */
export const submitPasswordForLogin = async ({
  password,
  confirmPassword,
  isSignup,
  phoneNumber,
  setLoading,
  setPassword,
  auth,
  dispatch,
  axiosInstance,
  cartItems,
  navigate,
  userResult,
  setLockUntil,
  setError,
}) => {

  if (isSignup) {
    const validation = validatePasswordComplete(password, confirmPassword);
    if (!validation.success) {
      notifyError(validation.message);
      setLoading(false);
      return false;
    }
  }

  if (password.length < 6) {
    notifyError('Le mot de passe doit contenir au moins 6 caractères');
    setLoading(false);
    return false;
  }

  const action = isSignup ? 'signup' : 'signin';

  const result = await handlePhoneLogin({
    auth,
    setLoading,
    dispatch,
    axiosInstance,
    cartItems,
    navigate,
    userResult,
    password,
    action,
    phoneNumber,
    setLockUntil,
    setError,
  });

  if (result) {
    setPassword('');
  }

  return result;
};

// ============================================
// STORAGE HELPERS
// ============================================

/**
 * Clears authentication storage data
 */
export const clearAuthStorage = () => {
  localStorage.removeItem(FIREBASE_STORAGE_KEYS.LAST_ATTEMPT_DATE);
  localStorage.removeItem(FIREBASE_STORAGE_KEYS.LOCK_UNTIL);
  localStorage.removeItem(FIREBASE_STORAGE_KEYS.RETRY_AFTER);
};

/**
 * Gets the current authentication step from storage
 */
export const getCurrentAuthStep = () => {
  return Number.parseInt(localStorage.getItem('currentAuthStep')) || AUTH_STEPS.PHONE_INPUT;
};

/**
 * Saves the current authentication step to storage
 */
export const saveCurrentAuthStep = (step) => {
  localStorage.setItem('currentAuthStep', step.toString());
};

/**
 * Clears the current authentication step from storage
 */
export const clearCurrentAuthStep = () => {
  localStorage.removeItem('currentAuthStep');
};

// ============================================
// OTP VERIFICATION (Forgot Password)
// ============================================

/**
 * Handles OTP verification for forgot password flow
 * @param {Object} params - Parameters object
 * @param {Object} params.auth - Firebase auth instance
 * @param {string} params.otp - OTP code
 * @param {Function} params.setLoading - Loading state setter
 * @param {Function} params.onSuccess - Success callback
 * @param {Function} params.onError - Error callback
 * @returns {Promise<Object>} - Verification result
 */
export const handleOtpVerification = async ({
  auth,
  otp,
  setLoading,
  onSuccess,
  onError,
}) => {
  if (otp.length !== VALIDATION_CONSTANTS.OTP_LENGTH) {
    return { success: false, error: 'Invalid OTP length' };
  }

  setLoading(true);

  try {
    const { verifyOTP } = require('../../pages/account/phoneNumberLogin/services/phoneAuthService');
    const result = await verifyOTP(auth, otp);

    if (result.user && result.success) {
      if (onSuccess) {
        onSuccess(result);
      }
      setLoading(false);
      return { success: true, user: result.user };
    }

    // Handle the error from verifyOTP result
    const errorMessage = result.error?.code || result.error || 'Invalid code';

    if (errorMessage.includes('code-expired')) {
      notifyError('Code expiré');
    } else if (errorMessage.includes('invalid-verification-code')) {
      notifyError(USER_MESSAGES.INCORRECT_CODE);
    } else {
      notifyError(USER_MESSAGES.VERIFICATION_ERROR);
    }

    if (onError) {
      onError(errorMessage);
    }
    setLoading(false);
    return { success: false, error: errorMessage };
  } catch (error) {
    console.error('OTP verification error:', error);

    const errorCode = error?.code || error?.message || 'unknown';

    if (errorCode.includes('code-expired')) {
      notifyError('Code expiré');
    } else if (errorCode.includes('invalid-verification-code')) {
      notifyError(USER_MESSAGES.INCORRECT_CODE);
    } else {
      notifyError(USER_MESSAGES.VERIFICATION_ERROR);
    }

    if (onError) {
      onError(errorCode);
    }
    setLoading(false);
    return { success: false, error: errorCode };
  }
};

/**
 * Handles forgot password flow (SMS + OTP + Password reset)
 * @param {Object} params - Parameters object
 * @param {string} params.phoneNumber - Phone number
 * @param {Object} params.auth - Firebase auth instance
 * @param {Object} params.axiosInstance - Axios instance
 * @param {Function} params.setLoading - Loading state setter
 * @param {string} params.recaptchaContainerId - ID for reCAPTCHA container
 * @returns {Promise<Object>} - Result object
 */
export const handleForgotPasswordFlow = async ({
  phoneNumber,
  auth,
  axiosInstance,
  setLoading,
  recaptchaContainerId = 'recaptcha-container',
}) => {
  const formattedNumber = phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`;

  if (!isPhoneValid(formattedNumber)) {
    notifyWarning(USER_MESSAGES.INVALID_NUMBER);
    return { success: false, error: ERROR_TYPES.INVALID_NUMBER };
  }

  setLoading(true);

  try {
    const userCheck = await checkIfUserPhoneExists(axiosInstance, formattedNumber);

    if (!userCheck.success) {
      notifyError('Numéro non trouvé');
      setLoading(false);
      return { success: false, error: 'User not found' };
    }

    const result = await phoneNumberSignUp(
      auth,
      formattedNumber,
      null,
      'forgot',
      recaptchaContainerId,
      setLoading
    );

    if (result?.success) {
      notifySuccess('Code envoyé par SMS');
      return { success: true, phoneNumber: formattedNumber };
    } else {
      handleSignUpError(result?.error);
      return { success: false, error: result?.error };
    }
  } catch (error) {
    console.error('Forgot password error:', error);
    notifyError('Erreur lors de l\'envoi du code');
    setLoading(false);
    return { success: false, error };
  }
};

